/* iter55f91 — Pop-out panel window.
 *
 * When the writer clicks the "Pop out" affordance on any workspace
 * panel header, we open this route in a new window (720×860 by
 * default) that renders the same panel in isolation. Keeps the writer
 * on a single React app / auth session — no cross-frame issues.
 *
 * The panelId ↔ component mapping is intentionally shallow. Only the
 * panels writers actually asked to detach are wired here. Anything
 * else falls back to a friendly "Panel not detachable" message.
 */
import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ChaptersPanel } from '@/components/panels/ChaptersPanel';
import { CharacterBiblePanel } from '@/components/panels/CharacterBiblePanel';
import NotebookPanel from '@/components/panels/NotebookPanel';
import { useAuth } from '@/lib/auth_context';

const PANEL_MAP = {
  chapters:      { title: 'Chapters',         Component: ChaptersPanel },
  characters:    { title: 'Character Bible',  Component: CharacterBiblePanel },
  notebook:      { title: 'Writer\u2019s Notebook', Component: NotebookPanel },
};

export default function PanelWindowPage() {
  const { panelId } = useParams();
  const { user, checking } = useAuth();
  const key = (panelId || '').replace(/^mode:/, '');
  const entry = PANEL_MAP[key];

  if (checking) return null;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="flex h-screen w-screen flex-col bg-background text-foreground" data-testid="panel-window">
      <div className="flex items-center justify-between border-b border-panel-border bg-panel-header/60 px-3 py-1.5">
        <span className="text-[11.5px] font-semibold uppercase tracking-wider text-foreground/85">
          {entry?.title || 'Panel not detachable'}
        </span>
        <Link to="/app" className="text-[11px] text-muted-foreground hover:underline" data-testid="panel-window-back">
          Back to workspace
        </Link>
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        {entry ? (
          <entry.Component onClose={() => window.close()} />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-[13px] text-muted-foreground">
            <p>This panel can\u2019t be popped out yet.</p>
            <Link to="/app" className="text-primary underline">Return to the workspace</Link>
          </div>
        )}
      </div>
    </div>
  );
}
